import { useEffect, useState, useCallback, useRef } from "react";
import { Plus, Pencil, Loader2, Trash2, Copy, Search, X, Power, Tag, BadgePercent, Sparkles, GripVertical, ListOrdered } from "lucide-react";
import { toast } from "sonner";
import { GlassCard } from "../../components/common/ui";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Switch } from "../../components/ui/switch";
import { Badge } from "../../components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../../components/ui/dialog";
import SingleImageUpload from "../../components/admin/SingleImageUpload";
import { formatGBP } from "../../components/common/StockBadge";
import api, { formatApiError } from "../../lib/api";

const inp = "mt-1 bg-black/30 border-white/15 text-white";
const TYPES = [
  ["bogof", "Buy One Get One Free"],
  ["fixed_bundle", "Fixed Bundle Price"],
  ["percentage", "Percentage Off"],
  ["amount_off", "Amount Off"],
  ["bogo_half", "Buy One Get One Half Price"],
  ["b2g1f", "Buy Two Get One Free"],
  ["half_price", "Half Price"],
];
const EMPTY = {
  name: "", promotion_type: "bogof", description: "", banner_image: "",
  product_ids: [], discount_percent: "", discount_amount: "", bundle_price: "",
  start_at: "", end_at: "", is_active: true, is_featured: false, priority: 0, display_order: 0,
};
const thumb = (p) => p?.images?.[0]?.thumb_url || p?.images?.[0]?.webp_url || p?.images?.[0]?.url || "";
const toLocal = (iso) => (iso ? String(iso).slice(0, 10) : "");

function ProductPicker({ selected, onChange }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!q.trim()) { setResults([]); return; }
    const t = setTimeout(() => {
      setLoading(true);
      api.get("/products", { params: { search: q, limit: 12 } })
        .then(({ data }) => setResults(data.items || [])).catch(() => {}).finally(() => setLoading(false));
    }, 300);
    return () => clearTimeout(t);
  }, [q]);

  const add = (p) => {
    if (selected.some((s) => s.product_id === p.product_id)) return;
    onChange([...selected, { product_id: p.product_id, name: p.name, sku: p.sku, retail_price: p.retail_price, image: thumb(p) }]);
  };
  const remove = (pid) => onChange(selected.filter((s) => s.product_id !== pid));

  return (
    <div className="space-y-3">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/40" />
        <Input data-testid="product-search-input" value={q} onChange={(e) => setQ(e.target.value)}
          placeholder="Search products by name or SKU…" className="pl-9 bg-black/30 border-white/15 text-white" />
      </div>
      {loading && <p className="text-xs text-white/40 flex items-center gap-1"><Loader2 className="h-3 w-3 animate-spin" /> Searching…</p>}
      {results.length > 0 && (
        <div className="rounded-lg border border-white/10 bg-black/30 max-h-56 overflow-y-auto divide-y divide-white/5" data-testid="product-search-results">
          {results.map((p) => {
            const on = selected.some((s) => s.product_id === p.product_id);
            return (
              <button key={p.product_id} type="button" onClick={() => add(p)} disabled={on}
                data-testid={`search-result-${p.product_id}`}
                className={`w-full flex items-center gap-3 p-2.5 text-left hover:bg-white/5 ${on ? "opacity-40 cursor-default" : ""}`}>
                <div className="h-10 w-10 rounded bg-white/10 overflow-hidden shrink-0 grid place-items-center">{thumb(p) ? <img src={thumb(p)} alt="" className="h-full w-full object-cover" /> : <Tag className="h-4 w-4 text-white/30" />}</div>
                <div className="min-w-0 flex-1"><p className="text-sm text-white truncate">{p.name}</p><p className="text-[11px] text-white/40">SKU {p.sku} · {formatGBP(p.retail_price)}</p></div>
                {on ? <span className="text-[11px] text-emerald-400">Added</span> : <Plus className="h-4 w-4 text-gold" />}
              </button>
            );
          })}
        </div>
      )}
      <div data-testid="selected-products">
        <p className="text-xs text-white/50 mb-2">{selected.length} product{selected.length === 1 ? "" : "s"} selected</p>
        <div className="space-y-2">
          {selected.map((p) => (
            <div key={p.product_id} className="flex items-center gap-3 rounded-lg border border-white/10 bg-white/5 p-2.5" data-testid={`selected-${p.product_id}`}>
              <div className="h-10 w-10 rounded bg-white/10 overflow-hidden shrink-0 grid place-items-center">{p.image ? <img src={p.image} alt="" className="h-full w-full object-cover" /> : <Tag className="h-4 w-4 text-white/30" />}</div>
              <div className="min-w-0 flex-1"><p className="text-sm text-white truncate">{p.name}</p><p className="text-[11px] text-white/40">SKU {p.sku} · {formatGBP(p.retail_price)}</p></div>
              <button type="button" onClick={() => remove(p.product_id)} data-testid={`remove-${p.product_id}`} className="text-white/50 hover:text-firered"><X className="h-4 w-4" /></button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function AdminPromotionEngine() {
  const [promos, setPromos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [selected, setSelected] = useState([]);
  const [filters, setFilters] = useState({ status: "all", promotion_type: "all", search: "" });
  const [orderDirty, setOrderDirty] = useState(false);
  const [savingOrder, setSavingOrder] = useState(false);
  const dragIndex = useRef(null);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const load = useCallback(() => {
    setLoading(true);
    const params = {};
    if (filters.status !== "all") params.status = filters.status;
    if (filters.promotion_type !== "all") params.promotion_type = filters.promotion_type;
    if (filters.search.trim()) params.search = filters.search.trim();
    api.get("/admin/product-promotions", { params }).then(({ data }) => { setPromos(data); setOrderDirty(false); }).catch(() => {}).finally(() => setLoading(false));
  }, [filters]);
  useEffect(() => { load(); }, [load]);

  const onDrop = (i) => {
    const from = dragIndex.current;
    dragIndex.current = null;
    if (from === null || from === i) return;
    setPromos((list) => {
      const next = [...list];
      const [m] = next.splice(from, 1);
      next.splice(i, 0, m);
      return next;
    });
    setOrderDirty(true);
  };

  const saveOrder = async () => {
    setSavingOrder(true);
    try {
      await api.post("/admin/product-promotions/reorder", { ordered_ids: promos.map((p) => p.promotion_id) });
      toast.success("Display order saved");
      setOrderDirty(false); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Could not save order"); }
    finally { setSavingOrder(false); }
  };

  const openNew = () => { setForm(EMPTY); setSelected([]); setEditingId(null); setOpen(true); };
  const openEdit = async (p) => {
    const { data } = await api.get(`/admin/product-promotions/${p.promotion_id}`);
    setForm({
      ...EMPTY, ...data,
      discount_percent: data.discount_percent ?? "", discount_amount: data.discount_amount ?? "",
      bundle_price: data.bundle_price ?? "", start_at: toLocal(data.start_at), end_at: toLocal(data.end_at),
    });
    setSelected(await hydrate(data.product_ids || []));
    setEditingId(data.promotion_id); setOpen(true);
  };

  const hydrate = async (ids) => {
    if (!ids.length) return [];
    // resolve product cards via the public list (search each id is not supported; use per-id detail)
    const out = [];
    for (const id of ids) {
      try {
        const { data } = await api.get(`/admin/products/${id}`);
        out.push({ product_id: id, name: data.name, sku: data.sku, retail_price: data.retail_price, image: thumb(data) });
      } catch {
        out.push({ product_id: id, name: id, sku: "", retail_price: 0, image: "" });
      }
    }
    return out;
  };

  const payload = () => {
    const p = {
      name: form.name.trim(), promotion_type: form.promotion_type, description: form.description || null,
      banner_image: form.banner_image || null, product_ids: selected.map((s) => s.product_id),
      start_at: form.start_at ? new Date(form.start_at + "T00:00:00").toISOString() : null,
      end_at: form.end_at ? new Date(form.end_at + "T23:59:59").toISOString() : null,
      is_active: form.is_active, is_featured: form.is_featured, priority: Number(form.priority) || 0,
      display_order: Number(form.display_order) || 0,
    };
    if (form.promotion_type === "percentage") p.discount_percent = Number(form.discount_percent) || 0;
    if (form.promotion_type === "amount_off") p.discount_amount = Number(form.discount_amount) || 0;
    if (form.promotion_type === "fixed_bundle") p.bundle_price = Number(form.bundle_price) || 0;
    return p;
  };

  const save = async () => {
    if (!form.name.trim()) { toast.error("Enter a promotion name"); return; }
    if (selected.length === 0) { toast.error("Select at least one product"); return; }
    if (!form.start_at || !form.end_at) { toast.error("Start date and end date are both required"); return; }
    setSaving(true);
    try {
      if (editingId) await api.patch(`/admin/product-promotions/${editingId}`, payload());
      else await api.post("/admin/product-promotions", payload());
      toast.success(editingId ? "Promotion updated" : "Promotion created");
      setOpen(false); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Save failed"); }
    finally { setSaving(false); }
  };

  const act = async (fn, okMsg) => { try { await fn(); toast.success(okMsg); load(); } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Action failed"); } };
  const toggle = (p) => act(() => api.post(`/admin/product-promotions/${p.promotion_id}/toggle`), p.is_active ? "Disabled" : "Enabled");
  const duplicate = (p) => act(() => api.post(`/admin/product-promotions/${p.promotion_id}/duplicate`), "Duplicated (disabled)");
  const remove = (p) => { if (window.confirm(`Delete “${p.name}”?`)) act(() => api.delete(`/admin/product-promotions/${p.promotion_id}`), "Deleted"); };

  const statusBadge = (s) => {
    const map = { active: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30", scheduled: "bg-gold/15 text-gold border-gold/30", expired: "bg-white/10 text-white/40 border-white/15", disabled: "bg-firered/10 text-firered border-firered/30" };
    return <Badge className={`border ${map[s] || map.disabled}`}>{s}</Badge>;
  };

  const t = form.promotion_type;
  const normalTotal = selected.reduce((a, b) => a + (b.retail_price || 0), 0);

  return (
    <div className="space-y-6" data-testid="admin-promotions">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="font-cinzel text-2xl sm:text-3xl text-white tracking-wide flex items-center gap-2"><BadgePercent className="h-6 w-6 text-gold" /> Promotions</h1>
          <p className="mt-2 text-muted-foreground">The single source of truth for all product offers, discounts and bundles. Applied automatically across the site.</p>
        </div>
        <Button data-testid="add-promotion-btn" onClick={openNew} className="cf-gold-btn font-semibold gap-2"><Plus className="h-4 w-4" /> New Promotion</Button>
      </div>

      {orderDirty && (
        <div className="flex items-center justify-between gap-3 rounded-lg border border-gold/30 bg-gold/5 px-4 py-2.5" data-testid="order-dirty-banner">
          <span className="text-sm text-gold">You've changed the display order. Save to apply it to the public Offers page.</span>
          <Button data-testid="save-order-btn" onClick={saveOrder} disabled={savingOrder} className="cf-gold-btn font-semibold gap-2">
            {savingOrder ? <Loader2 className="h-4 w-4 animate-spin" /> : <ListOrdered className="h-4 w-4" />} Save Order
          </Button>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/40" />
          <Input data-testid="promo-filter-search" value={filters.search} onChange={(e) => setFilters((f) => ({ ...f, search: e.target.value }))} placeholder="Search promotions…" className="pl-9 bg-black/30 border-white/15 text-white" />
        </div>
        <Select value={filters.status} onValueChange={(v) => setFilters((f) => ({ ...f, status: v }))}>
          <SelectTrigger className="w-40 bg-black/30 border-white/15 text-white" data-testid="promo-filter-status"><SelectValue /></SelectTrigger>
          <SelectContent>{["all", "active", "scheduled", "expired", "disabled"].map((s) => <SelectItem key={s} value={s} className="capitalize">{s === "all" ? "All statuses" : s}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={filters.promotion_type} onValueChange={(v) => setFilters((f) => ({ ...f, promotion_type: v }))}>
          <SelectTrigger className="w-52 bg-black/30 border-white/15 text-white" data-testid="promo-filter-type"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">All types</SelectItem>{TYPES.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      <GlassCard className="p-0 overflow-hidden" hover={false}>
        {loading ? (
          <div className="p-8 flex items-center gap-2 text-muted-foreground"><Loader2 className="h-5 w-5 animate-spin text-gold" /> Loading…</div>
        ) : promos.length === 0 ? (
          <div className="p-12 text-center text-muted-foreground"><Sparkles className="h-8 w-8 mx-auto text-gold/60" /><p className="mt-3">No promotions yet. Create your first offer.</p></div>
        ) : (
          <Table>
            <TableHeader><TableRow className="border-white/10 hover:bg-transparent">
              <TableHead className="text-white/70 w-8"></TableHead>
              <TableHead className="text-white/70">Promotion</TableHead>
              <TableHead className="text-white/70">Type</TableHead>
              <TableHead className="text-white/70 text-center">Products</TableHead>
              <TableHead className="text-white/70">Status</TableHead>
              <TableHead className="text-white/70 text-center">Display Order</TableHead>
              <TableHead className="text-white/70 text-right">Actions</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {promos.map((p, idx) => (
                <TableRow key={p.promotion_id} className="border-white/10" data-testid={`promo-row-${p.promotion_id}`}
                  draggable onDragStart={() => { dragIndex.current = idx; }} onDragOver={(e) => e.preventDefault()} onDrop={() => onDrop(idx)}>
                  <TableCell className="w-8 cursor-grab active:cursor-grabbing text-white/40" data-testid={`drag-handle-${p.promotion_id}`} title="Drag to reorder"><GripVertical className="h-4 w-4" /></TableCell>
                  <TableCell className="text-white font-medium">{p.name}{p.is_featured && <span className="ml-2 text-[10px] text-gold border border-gold/40 rounded-full px-2 py-0.5">FEATURED</span>}<div className="text-[11px] text-gold font-normal">{p.ribbon_label}</div></TableCell>
                  <TableCell className="text-muted-foreground text-sm">{p.type_label}</TableCell>
                  <TableCell className="text-center text-white/80">{p.product_count}</TableCell>
                  <TableCell>{statusBadge(p.status)}</TableCell>
                  <TableCell className="text-center text-white/80" data-testid={`display-order-${p.promotion_id}`}>{p.display_order ? p.display_order : "—"}</TableCell>
                  <TableCell className="text-right space-x-1">
                    <button onClick={() => toggle(p)} title={p.is_active ? "Disable" : "Enable"} data-testid={`toggle-${p.promotion_id}`} className={`inline-grid place-items-center h-8 w-8 rounded-md hover:bg-white/10 ${p.is_active ? "text-emerald-400" : "text-white/40"}`}><Power className="h-4 w-4" /></button>
                    <button onClick={() => openEdit(p)} title="Edit" data-testid={`edit-${p.promotion_id}`} className="inline-grid place-items-center h-8 w-8 rounded-md text-white/60 hover:text-gold hover:bg-white/10"><Pencil className="h-4 w-4" /></button>
                    <button onClick={() => duplicate(p)} title="Duplicate" data-testid={`duplicate-${p.promotion_id}`} className="inline-grid place-items-center h-8 w-8 rounded-md text-white/60 hover:text-gold hover:bg-white/10"><Copy className="h-4 w-4" /></button>
                    <button onClick={() => remove(p)} title="Delete" data-testid={`delete-${p.promotion_id}`} className="inline-grid place-items-center h-8 w-8 rounded-md text-white/60 hover:text-firered hover:bg-firered/10"><Trash2 className="h-4 w-4" /></button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </GlassCard>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="cf-glass border-white/10 text-white max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="promo-dialog">
          <DialogHeader>
            <DialogTitle className="font-cinzel text-xl">{editingId ? "Edit Promotion" : "New Promotion"}</DialogTitle>
            <DialogDescription className="text-white/50">Product-only. A product can belong to just one active promotion at a time.</DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <div><Label className="text-white/80">Name *</Label><Input data-testid="promo-name" value={form.name} onChange={(e) => set("name", e.target.value)} className={inp} /></div>
              <div>
                <Label className="text-white/80">Type *</Label>
                <Select value={form.promotion_type} onValueChange={(v) => set("promotion_type", v)}>
                  <SelectTrigger className={inp} data-testid="promo-type"><SelectValue /></SelectTrigger>
                  <SelectContent>{TYPES.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>

            {/* type-specific value */}
            {t === "percentage" && <div><Label className="text-white/80">Percentage off (%)</Label><Input data-testid="promo-percent" type="number" value={form.discount_percent} onChange={(e) => set("discount_percent", e.target.value)} className={inp} /></div>}
            {t === "amount_off" && <div><Label className="text-white/80">Amount off (£, per eligible product)</Label><Input data-testid="promo-amount" type="number" value={form.discount_amount} onChange={(e) => set("discount_amount", e.target.value)} className={inp} /></div>}
            {t === "fixed_bundle" && (
              <div>
                <Label className="text-white/80">Bundle price (£)</Label>
                <Input data-testid="promo-bundle-price" type="number" value={form.bundle_price} onChange={(e) => set("bundle_price", e.target.value)} className={inp} />
                <p className="text-xs mt-1 text-white/50">Normal total of selected products: <span className="text-white">{formatGBP(normalTotal)}</span>. Bundle price must be lower.</p>
              </div>
            )}

            <div><Label className="text-white/80">Description (optional)</Label><Textarea data-testid="promo-description" value={form.description} onChange={(e) => set("description", e.target.value)} rows={2} className={inp} /></div>

            <div>
              <Label className="text-white/80 mb-2 block">Products *</Label>
              <ProductPicker selected={selected} onChange={setSelected} />
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div><Label className="text-white/80">Start date *</Label><Input data-testid="promo-start" type="date" value={form.start_at} onChange={(e) => set("start_at", e.target.value)} className={`${inp} [color-scheme:dark]`} /></div>
              <div><Label className="text-white/80">End date *</Label><Input data-testid="promo-end" type="date" value={form.end_at} onChange={(e) => set("end_at", e.target.value)} className={`${inp} [color-scheme:dark]`} /></div>
            </div>

            <div><Label className="text-white/80">Banner image (optional)</Label><div className="mt-2"><SingleImageUpload value={form.banner_image} onChange={(u) => set("banner_image", u)} testid="promo-banner" /></div></div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div><Label className="text-white/80">Priority (pricing stack)</Label><Input data-testid="promo-priority" type="number" value={form.priority} onChange={(e) => set("priority", e.target.value)} className={inp} /></div>
              <div><Label className="text-white/80">Display Order (Offers page)</Label><Input data-testid="promo-display-order" type="number" value={form.display_order} onChange={(e) => set("display_order", e.target.value)} className={inp} /><p className="text-[11px] mt-1 text-white/40">Lower shows first. 0 = unordered (shown after ordered promotions).</p></div>
            </div>
            <div className="flex flex-wrap gap-6">
              <label className="flex items-center gap-2 text-sm text-white/85"><Switch data-testid="promo-active" checked={form.is_active} onCheckedChange={(v) => set("is_active", v)} /> Enabled</label>
              <label className="flex items-center gap-2 text-sm text-white/85"><Switch data-testid="promo-featured" checked={form.is_featured} onCheckedChange={(v) => set("is_featured", v)} /> Featured</label>
            </div>
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)} className="text-white/70">Cancel</Button>
            <Button data-testid="save-promo-btn" onClick={save} disabled={saving} className="cf-gold-btn font-semibold gap-2">{saving && <Loader2 className="h-4 w-4 animate-spin" />} Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
